// src/services/gasUsage.js
//
// Registrazione del gas usato e recuperato durante un intervento.
//
// Sta qui e non dentro una pagina perche' serve anche alla coda offline:
// il calcolo legge le giacenze correnti, le modifica e le riscrive, quindi
// non si puo' preparare in anticipo sul dispositivo. Va rifatto nel momento
// in cui il rapportino arriva davvero al server.
//
// La logica e' quella della pagina ManutenzioniAssegnate, che era la piu'
// completa delle quattro varianti in giro: e' l'unica che gestisce il caso
// dello stesso gas usato e recuperato insieme, e che crea la voce se il
// tecnico non ce l'ha ancora sul furgone.

import { authFetch } from '../utils/authFetch';

const API = '/api';

function nomeUtente(utente) {
  const nome = `${utente?.nome || ''} ${utente?.cognome || ''}`.trim();
  return nome || utente?.username || '';
}

function adesso() {
  return new Date().toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const numero = (v) => Math.round(parseFloat(String(v || '').replace(',', '.')));

/**
 * @param {Object} p
 * @param {string} p.gasUsedName
 * @param {string|number} p.gasUsedAmount
 * @param {string} p.gasRecoveredName
 * @param {string|number} p.gasRecoveredAmount
 * @param {string} p.contesto  "una manutenzione" oppure "un intervento"
 * @returns {Promise<{registrato: boolean, messaggio?: string}>}
 */
export async function registraGas({
  gasUsedName = '',
  gasUsedAmount = '',
  gasRecoveredName = '',
  gasRecoveredAmount = '',
  contesto = 'un intervento',
}) {
  const nomeUsato = String(gasUsedName || '').trim();
  const nomeRecuperato = String(gasRecoveredName || '').trim();

  const quantitaUsata = numero(gasUsedAmount);
  const quantitaRecuperata = numero(gasRecoveredAmount);

  const cUso = Boolean(nomeUsato) && !Number.isNaN(quantitaUsata) && quantitaUsata > 0;
  const cRecupero =
    Boolean(nomeRecuperato) && !Number.isNaN(quantitaRecuperata) && quantitaRecuperata > 0;

  // niente da registrare: non e' un errore, il gas non e' obbligatorio
  if (!cUso && !cRecupero) return { registrato: false };

  const rispostaUtente = await authFetch(`${API}/user`);
  if (!rispostaUtente?.ok) throw new Error('Non riesco a leggere l utente per aggiornare i gas');
  const utente = await rispostaUtente.json();
  const idUtente = utente.id || utente._id;

  const rispostaGas = await authFetch(`${API}/gas`);
  if (!rispostaGas?.ok) throw new Error('Non riesco a leggere le giacenze gas');
  const giacenze = await rispostaGas.json();

  const mio = (nome) =>
    giacenze.find((g) => {
      const uid = g.user?._id || g.user?.id;
      return uid === idUtente && g.gasName === nome;
    });

  const aggiorna = (id, grammi) =>
    authFetch(`${API}/gas/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantityGrams: Math.max(0, grammi) }),
    });

  const crea = (nome, grammi) =>
    authFetch(`${API}/gas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gasName: nome, quantityGrams: Math.max(0, grammi) }),
    });

  const pezzi = [];

  const stessoGas = cUso && cRecupero && nomeUsato === nomeRecuperato;

  if (stessoGas || (cUso && !cRecupero) || (!cUso && cRecupero)) {
    // un solo gas: la giacenza si muove del saldo netto
    const nome = stessoGas ? nomeUsato : cUso ? nomeUsato : nomeRecuperato;
    const usati = cUso ? quantitaUsata : 0;
    const recuperati = cRecupero ? quantitaRecuperata : 0;

    const voce = mio(nome);
    const attuale = voce ? Number(voce.quantityGrams || 0) : 0;
    const finale = attuale + recuperati - usati;

    if (!voce && usati > recuperati) {
      console.warn('[GAS] uso piu gas di quello registrato, e non risulta nessuna voce per', nome);
    } else if (usati === recuperati && usati > 0) {
      // usato e recuperato in pari: la giacenza non cambia
    } else if (!voce) {
      await crea(nome, finale);
    } else {
      await aggiorna(voce._id, finale);
    }

    if (cUso) pezzi.push(`utilizzati ${usati} g di ${nome}`);
    if (cRecupero) pezzi.push(`recuperati ${recuperati} g di ${nome}`);
  } else if (cUso && cRecupero) {
    // due gas diversi: si trattano separatamente
    const voceUso = mio(nomeUsato);
    if (!voceUso) {
      console.warn('[GAS] nessuna voce per il gas usato:', nomeUsato);
    } else {
      await aggiorna(voceUso._id, Number(voceUso.quantityGrams || 0) - quantitaUsata);
      pezzi.push(`utilizzati ${quantitaUsata} g di ${nomeUsato}`);
    }

    const voceRecupero = mio(nomeRecuperato);
    if (!voceRecupero) {
      await crea(nomeRecuperato, quantitaRecuperata);
    } else {
      await aggiorna(
        voceRecupero._id,
        Number(voceRecupero.quantityGrams || 0) + quantitaRecuperata
      );
    }
    pezzi.push(`recuperati ${quantitaRecuperata} g di ${nomeRecuperato}`);
  }

  if (pezzi.length === 0) return { registrato: false };

  const chi = nomeUtente(utente);
  const messaggio = `${chi ? `${chi} ha ` : ''}${pezzi.join(' e ')} durante ${contesto} il ${adesso()}.`;

  await authFetch(`${API}/notifications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      type: 'gas_usage',
      message: messaggio,
      roles: ['admin', 'gestione', 'segreteria'],
    }),
  });

  return { registrato: true, messaggio };
}
